import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Iconify from '../../../components/Iconify';
import { subHolder, subOccupant } from '../../../helpers/subscriptions';
import Label from '../../../components/Label';

const fEur = (n) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const fInt = (n) => new Intl.NumberFormat('es-ES').format(Math.round(Number(n) || 0));
const fNum = (n) =>
  new Intl.NumberFormat('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n) || 0);

const intervalLabel = (i) => {
  if (i === 'month') return 'Mensual';
  if (i === 'year') return 'Anual';
  if (i === 'one_time') return 'Pago único';
  return '—';
};

// Asientos efectivos: lo que realmente paga la sub. Si maxSeats es null lo tratamos como
// "sin límite" y caemos en userCount como aproximación; si tampoco hay, asumimos 1.
const effectiveSeats = (sub, stripeInfo) => {
  if (sub.maxSeats != null) return Number(sub.maxSeats) || 0;
  if (stripeInfo?.qty != null) return Number(stripeInfo.qty) || 0;
  if (sub.userCount != null) return Number(sub.userCount) || 0;
  return 1;
};

// Importe de un ciclo (mensual/anual/único) para subs manuales.
const manualCycleAmountEur = (sub) => {
  const flat = sub.flatPriceEur != null && sub.flatPriceEur !== '' ? Number(sub.flatPriceEur) : null;
  if (flat != null && !Number.isNaN(flat)) return flat;
  const perUser = sub.pricePerUserEur != null && sub.pricePerUserEur !== '' ? Number(sub.pricePerUserEur) : null;
  if (perUser != null && !Number.isNaN(perUser)) return perUser * effectiveSeats(sub);
  return null;
};

// Calcula la fila/aportación de una sub. Devuelve siempre algo (incluso si está sin info)
// para poder pintar la tabla de detalle. `priced` indica si entra en MRR/ARR/ticket/ARPU.
const computeSubRow = (sub, lookup) => {
  const stripeInfo = sub.stripeSubscriptionId ? lookup?.get(sub.stripeSubscriptionId) : null;
  const seats = effectiveSeats(sub, stripeInfo);

  const manualCycle = manualCycleAmountEur(sub);
  if (manualCycle != null && sub.billingInterval) {
    let mrr = null;
    if (sub.billingInterval === 'month') mrr = manualCycle;
    else if (sub.billingInterval === 'year') mrr = manualCycle / 12;
    else if (sub.billingInterval === 'one_time') mrr = 0;
    if (mrr != null) {
      return {
        source: 'manual',
        billingInterval: sub.billingInterval,
        cycleEur: manualCycle,
        seats,
        mrr,
        priced: true,
      };
    }
  }

  if (stripeInfo) {
    return {
      source: 'stripe',
      billingInterval: stripeInfo.billingInterval,
      cycleEur: stripeInfo.cycleAmountEur != null ? Number(stripeInfo.cycleAmountEur) : null,
      seats,
      mrr: Number(stripeInfo.mrrEur) || 0,
      priced: true,
    };
  }

  return {
    source: sub.stripeSubscriptionId ? 'stripe' : 'manual',
    billingInterval: sub.billingInterval || null,
    cycleEur: null,
    seats,
    mrr: null,
    priced: false,
  };
};

export default function SelectionMetrics({ items, onClear, stripeStatsLookup }) {
  const [open, setOpen] = useState(false);

  const totalSelected = items.length;
  if (totalSelected === 0) return null;

  let mrrTotal = 0;
  let pricedCount = 0;
  let recurringCount = 0; // subs con MRR > 0 — entran en ticket medio
  let recurringSeats = 0; // asientos de las recurrentes — denominador de ARPU
  let seatsTotal = 0;

  const rows = items.map((sub) => {
    const row = computeSubRow(sub, stripeStatsLookup);
    seatsTotal += row.seats;
    if (row.priced) {
      pricedCount += 1;
      mrrTotal += row.mrr;
      if (row.mrr > 0) {
        recurringCount += 1;
        recurringSeats += row.seats;
      }
    }
    return { sub, row };
  });

  const arr = mrrTotal * 12;
  const avgTicket = recurringCount > 0 ? mrrTotal / recurringCount : 0;
  const avgSeats = totalSelected > 0 ? seatsTotal / totalSelected : 0;
  const arpu = recurringSeats > 0 ? mrrTotal / recurringSeats : 0;
  const missingCount = totalSelected - pricedCount;

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Box
        sx={{
          p: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          bgcolor: 'background.neutral',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="eva:checkmark-square-2-outline" width={20} height={20} sx={{ color: 'text.secondary' }} />
            <Typography variant="subtitle2" color="text.primary">
              {totalSelected} suscripción{totalSelected === 1 ? '' : 'es'} seleccionada{totalSelected === 1 ? '' : 's'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => setOpen((v) => !v)}
              startIcon={
                <Iconify
                  icon={open ? 'eva:arrow-ios-upward-outline' : 'eva:arrow-ios-downward-outline'}
                  width={16}
                  height={16}
                />
              }
            >
              {open ? 'Ocultar detalle' : 'Ver detalle'}
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={onClear}
              startIcon={<Iconify icon="eva:close-outline" width={16} height={16} />}
            >
              Limpiar
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <MetricBox label="MRR teórico" value={fEur(mrrTotal)} />
          <MetricBox label="ARR" value={fEur(arr)} />
          <MetricBox
            label="Ticket medio"
            value={fEur(avgTicket)}
            hint="MRR teórico / nº subs recurrentes con precio (excluye pago único y subs sin info)."
          />
          <MetricBox
            label="ARPU"
            value={fEur(arpu)}
            hint="MRR teórico / asientos de las subs recurrentes con precio. Ingreso medio por asiento ocupado (ponderado)."
          />
          <MetricBox label="Asientos totales" value={fInt(seatsTotal)} />
          <MetricBox label="Asientos / sub" value={fNum(avgSeats)} />
          <MetricBox
            label="Subs con precio"
            value={`${fInt(pricedCount)} / ${fInt(totalSelected)}`}
            hint="Subs con billingInterval y tarifa configurados. El resto no aporta a MRR/ARR/ticket/ARPU."
          />
        </Grid>

        {missingCount > 0 && (
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
            {missingCount} suscripción{missingCount === 1 ? '' : 'es'} sin tarifa configurada — no cuentan para
            MRR/ARR/ticket/ARPU. Las manuales requieren precio + facturación; las Stripe deben aparecer en stripe-stats
            (sincroniza si faltan).
          </Alert>
        )}

        <Collapse in={open} unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Detalle por suscripción — datos usados en el cálculo. Una sub aporta a MRR/ARR/ticket/ARPU solo si tiene
            tarifa e intervalo (manual configurado, o Stripe activa con stats sincronizadas).
          </Typography>
          <TableContainer
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sub</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell>Intervalo</TableCell>
                  <TableCell align="right">Ciclo</TableCell>
                  <TableCell align="right">Asientos</TableCell>
                  <TableCell align="right">MRR</TableCell>
                  <TableCell align="right">ARR</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Valor que esta sub aporta al ticket medio (= su MRR si es recurrente con precio).">
                      <span>Ticket medio</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="ARPU de esta sub: MRR / asientos. Mide cuánto paga por asiento. El ARPU agregado es ponderado por asientos, no la media de esta columna.">
                      <span>ARPU</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(({ sub, row }) => {
                  const isRecurring = row.priced && row.mrr > 0;
                  const labelColor = !row.priced ? 'warning' : row.source === 'manual' ? 'default' : 'info';
                  const labelText = row.priced ? row.source : 'sin info';
                  // Titular primero (de quién es la sub) y ocupante como respaldo: en una huérfana
                  // solo hay titular, y en una de equipo con 2+ ocupantes no hay ninguno de los dos.
                  const subInfo = subHolder(sub)?.email || subOccupant(sub)?.email || sub.workspace?.name || '—';
                  return (
                    <TableRow key={sub.id} hover>
                      <TableCell>
                        <Stack spacing={0}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {String(sub.id).slice(0, 8)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" noWrap>
                            {subInfo}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Label color={labelColor}>{labelText}</Label>
                      </TableCell>
                      <TableCell>{intervalLabel(row.billingInterval)}</TableCell>
                      <TableCell align="right">{row.cycleEur != null ? fEur(row.cycleEur) : '—'}</TableCell>
                      <TableCell align="right">{fInt(row.seats)}</TableCell>
                      <TableCell align="right">{row.mrr != null ? fEur(row.mrr) : '—'}</TableCell>
                      <TableCell align="right">{row.mrr != null ? fEur(row.mrr * 12) : '—'}</TableCell>
                      <TableCell align="right">
                        {isRecurring ? (
                          fEur(row.mrr)
                        ) : (
                          <Tooltip
                            title={row.priced ? 'Pago único — no aporta a ticket medio' : 'Sin tarifa — no aporta'}
                          >
                            <span>—</span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isRecurring && row.seats > 0 ? (
                          fEur(row.mrr / row.seats)
                        ) : (
                          <Tooltip
                            title={
                              row.priced ? 'Pago único o sin asientos — no aporta a ARPU' : 'Sin tarifa — no aporta'
                            }
                          >
                            <span>—</span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Box>
    </Box>
  );
}

SelectionMetrics.propTypes = {
  items: PropTypes.array.isRequired,
  onClear: PropTypes.func.isRequired,
  stripeStatsLookup: PropTypes.instanceOf(Map),
};

function MetricBox({ label, value, hint }) {
  return (
    <Grid item xs={6} sm={4} md={2}>
      <Stack spacing={0.25}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
          {hint && (
            <Tooltip title={hint}>
              <span style={{ display: 'inline-flex' }}>
                <Iconify
                  icon="eva:info-outline"
                  width={12}
                  height={12}
                  sx={{ color: 'text.disabled', cursor: 'help' }}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="h6" color="text.primary" sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Stack>
    </Grid>
  );
}

MetricBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
};
